import { isBefore } from "date-fns"

import { SprintInfo, SprintStatus } from "@/models/Sprint"
import { emitterEmit } from "@/plugins/mitt-emitter"
import { getTodayCalendarDate, toCalendarDate } from "@/utils/dateUtil"

import { StructuredDescription } from "./EditorData"
import { Epic } from "./Epic"
import {
  FilterBacklog,
  FilterBacklogMetrics,
  ItemQueryFilter,
  ItemQueryPriorityType
} from "./Filter"
import { FeContext, Item, ItemNotInProgressStatuses, ItemStatus, ItemType } from "./Item"
import { Milestone } from "./Milestone"
import { TeamType } from "./Team"

export type StoryAgeType =
  | "bug"
  | "devOps"
  | "incident"
  | "storyAndSpike"
  | "supportCase"
  | "techDebt"
  | "designDebt"

export type StoreMutation =
  | "setBacklog"
  | "setUnprioritizedBacklog"
  | "setSprintBacklog"
  | "setEpicBacklog"
  | "setMilestones"
  | "setEpicRoadmap"
  | "setMyWork"

export interface BacklogAnalyticsPeriod {
  start: number
  end: number
  metrics: BacklogAnalyticsMetrics
  startIndex: number
  endIndex: number
}

export interface ItemsCountWithoutSuccess {
  itemsCount: number
  projectNames: string[]
}

export interface BacklogAnalyticsChart {
  [key: string]: BacklogAnalyticsMetrics
}

export class BacklogAnalyticsMetrics {
  metrics: Partial<Record<ItemType, number>> = {}
  total = 0

  constructor(props?: Partial<BacklogAnalyticsMetrics>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class BacklogAnalytics {
  chartInfo: BacklogAnalyticsChart = {}
  periods: BacklogAnalyticsPeriod[] = []
  general: BacklogAnalyticsMetrics = new BacklogAnalyticsMetrics()
  generalByTeamType: Partial<Record<TeamType, BacklogAnalyticsMetrics>> = {}

  constructor(props?: Partial<BacklogAnalytics>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.generalByTeamType = Object.entries(this.generalByTeamType).reduce(
      (acc, [teamType, metrics]) => {
        acc[teamType as TeamType] = new BacklogAnalyticsMetrics(metrics)
        return acc
      },
      {} as Partial<Record<TeamType, BacklogAnalyticsMetrics>>
    )
  }
}

export interface Backlog {
  items: Item[]
  size?: number
  storyPoints?: number
  estimatedHours?: number
  notPrioritizedItemsCount?: number
  nonEstimatedTaskCount?: number
  unassignedItemsCount?: number
}

export interface ChangePriorityParams {
  itemPriority: ItemQueryPriority
  backlogSetup?: BacklogSetup
  priorityMessageParams?: ChangePriorityMessageParams
  feContext?: FeContext
}

export interface UpdateElementInBacklog {
  item: Item | Epic | Milestone
  storeBacklogId: string
  sprintId?: string
}

export interface InsertItemInBacklog {
  item: Item
  index: number | null
  backlogName: string
  sprintId?: string
  currentMaxLength?: number
}

export interface InsertEpicInBacklog {
  item: Epic
  index: number | null
  backlogName: string
  sprintId?: string
  currentMaxLength?: number
}

export interface RemoveItemFromBacklog {
  itemId: string
  nameBacklog: string
  sprintId?: string
}

export interface GetItemCurrentPosition {
  projectId: string
  itemId: string
  filter: FilterBacklog
}

export interface ItemCurrentPositionResponse {
  position: number
  offsetPosition: number
}

export interface ServerPriorityResponse {
  newPriority: number
  normalizedAll: boolean
}

export interface EpicMilestonePriority {
  epicId?: string
  milestoneId?: string
}

export interface BugWaterline {
  label: string
  severityKey: string
  currentValue: number
  maxValue: number
}

export interface ItemMoveTo {
  from: ItemQueryPriorityType
  fromSprintId?: string
  fromSprintInfo?: SprintInfo
  to: ItemQueryPriorityType
  toSprintId?: string
  toSprintStatus?: SprintStatus
  toSprintSequenceNumber?: number
  toSprintInfo?: SprintInfo
  position?: "TOP" | "BOTTOM"
  item: Item
  nextItemId?: string
  previousItemId?: string
  newIndex?: number
  currentIndex?: number
  message?: StructuredDescription
}

export class ItemQueryPriority {
  previousItemId?: string | null
  itemId = ""
  nextItemId?: string | null
  priorityType: ItemQueryPriorityType = "BACKLOG"
  fromPriorityType?: ItemQueryPriorityType
  epicId?: string
  lowest?: boolean
  highest?: boolean
  sprintId?: string
  fromSprintId?: string
  milestoneId?: string
  item?: Item | Epic
  backlogSetup?: BacklogSetup
  message?: StructuredDescription
  projectId?: string
  backlogSetupId?: string

  constructor(props?: Partial<ItemQueryPriority>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  getJsonObj() {
    return {
      previousItemId: this.previousItemId,
      itemId: this.itemId,
      nextItemId: this.nextItemId,
      priorityType: this.priorityType,
      epicId: this.epicId && this.epicId.length ? this.epicId : undefined,
      lowest: this.lowest,
      highest: this.highest,
      sprintId: this.sprintId,
      message: this.message
    }
  }
}

export type BacklogConfigType = {
  acceptType: ItemType[]
  elementsType: BacklogElementType
  backlogGetAction: string
  backlogMetrics?: string
  storeMutation: StoreMutation
  storeGetter: string
  storeContentGetter?: string
  storeBacklogId: string
  filter: ItemQueryFilter
  priorityType: ItemQueryPriorityType
  priorityAction: string
  priorityBatchAction?: string
  addAction?: string
  removeAction?: string
  putIntoBacklog?: boolean
  sortBacklog?: boolean
  headerComponentName: string
  backlogSetupId?: string
}

type BacklogType =
  | "backlogProduct"
  | "backlogUnprioritized"
  | "backlogBug"
  | "backlogIncident"
  | "backlogSprint"
  | "backlogEpic"
  | "backlogSupportCases"
  | "backlogTechDebt"
  | "backlogDesignDebt"
  | "backlogDev"
  | "backlogDevOps"
  | "backlogOps"
  | "roadmapEpic"
  | "backlogStoryPoker"
  | "backlogMilestones"
  | "backlogMyWork"
  | "backlogProductAll"
  | "backlogSingleListView"
type BacklogConfig = Record<BacklogType, BacklogConfigType>

type BacklogElementType = "ITEM" | "EPIC"

const backlogConfigs: BacklogConfig = {
  backlogStoryPoker: {
    acceptType: [
      "STORY",
      "TASK",
      "BUG",
      "SPIKE",
      "SUPPORT_CASE",
      "DEV",
      "DEV_OPS",
      "OPS",
      "TECH_DEBT",
      "DESIGN_DEBT",
      "INCIDENT"
    ],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    storeMutation: "setBacklog",
    storeGetter: "GET_STORY_POKER_BACKLOG",
    storeContentGetter: "GET_STORY_POKER_BACKLOG_CONTENT",
    storeBacklogId: "backlogStoryPoker",
    filter: {
      hasActiveStoryPoker: true,
      includeTeamLessSupportCases: true,
      size: 20,
      hasBacklogPriority: true,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: false,
    headerComponentName: "BacklogStoryPokerHeader"
  },
  backlogProduct: {
    acceptType: ["STORY", "SPIKE"],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "ITEMS_METRICS",
    storeMutation: "setBacklog",
    storeGetter: "GET_PRODUCT_BACKLOG",
    storeContentGetter: "GET_PRODUCT_BACKLOG_CONTENT",
    storeBacklogId: "backlogProduct",
    filter: {
      itemTypes: ["STORY", "SPIKE"],
      hasBacklogPriority: true,
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogMetricHeader"
  },
  backlogUnprioritized: {
    acceptType: [
      "STORY",
      "TASK",
      "BUG",
      "SPIKE",
      "SUPPORT_CASE",
      "DEV",
      "DEV_OPS",
      "OPS",
      "TECH_DEBT",
      "DESIGN_DEBT",
      "INCIDENT"
    ],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    storeMutation: "setUnprioritizedBacklog",
    storeGetter: "GET_UNPRIORITIZED_BACKLOG",
    storeContentGetter: "GET_UNPRIORITIZED_BACKLOG_CONTENT",
    storeBacklogId: "backlogUnprioritized",
    filter: {
      itemTypes: [],
      hasBacklogPriority: false,
      size: 100,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      sortKey: ["unprioritizedOrder"]
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: false,
    sortBacklog: false,
    headerComponentName: "BacklogUnprioritizedHeader"
  },
  backlogBug: {
    acceptType: ["BUG"],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "METRICS_BUG_SEVERITY",
    storeMutation: "setBacklog",
    storeGetter: "GET_BUG_BACKLOG",
    storeContentGetter: "GET_BUG_BACKLOG_CONTENT",
    storeBacklogId: "backlogBug",
    filter: {
      itemTypes: ["BUG"],
      hasBacklogPriority: true,
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogBugComponent"
  },
  backlogIncident: {
    acceptType: ["INCIDENT"],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "ITEMS_METRICS",
    storeMutation: "setBacklog",
    storeGetter: "GET_INCIDENT_BACKLOG",
    storeContentGetter: "GET_INCIDENT_BACKLOG_CONTENT",
    storeBacklogId: "backlogIncident",
    filter: {
      itemTypes: ["INCIDENT"],
      hasBacklogPriority: true,
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogMetricHeader"
  },
  backlogSprint: {
    acceptType: [
      "STORY",
      "TASK",
      "BUG",
      "SPIKE",
      "SUPPORT_CASE",
      "DEV",
      "DEV_OPS",
      "OPS",
      "TECH_DEBT",
      "DESIGN_DEBT",
      "INCIDENT"
    ],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG_BY_ACCOUNT",
    backlogMetrics: "SPRINT_METRICS",
    storeMutation: "setSprintBacklog",
    storeGetter: "GET_SPRINT_BACKLOG",
    storeBacklogId: "backlogSprint",
    filter: {
      sortKey: ["sprintPriority"],
      size: 20,
      inSprint: true,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      hasBacklogPriority: true,
      withLatestStatusChangeComment: true
    },
    priorityType: "SPRINT",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    addAction: "ADD_SPRINT_ITEM",
    removeAction: "REMOVE_SPRINT_ITEM",
    putIntoBacklog: true,
    headerComponentName: "BacklogSprintHeader"
  },

  backlogSupportCases: {
    acceptType: ["SUPPORT_CASE"],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "METRICS_SUPPORT_CASE",
    storeMutation: "setBacklog",
    storeGetter: "GET_SUPPORT_CASE_BACKLOG",
    storeContentGetter: "GET_SUPPORT_CASE_BACKLOG_CONTENT",
    storeBacklogId: "backlogSupportCases",
    filter: {
      itemTypes: ["SUPPORT_CASE"],
      hasBacklogPriority: true,
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogBugComponent"
  },
  backlogTechDebt: {
    acceptType: ["TECH_DEBT"],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "ITEMS_METRICS",
    storeMutation: "setBacklog",
    storeGetter: "GET_TECH_DEBT_BACKLOG",
    storeContentGetter: "GET_TECH_DEBT_BACKLOG_CONTENT",
    storeBacklogId: "backlogTechDebt",
    filter: {
      itemTypes: ["TECH_DEBT"],
      hasBacklogPriority: true,
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogMetricHeader"
  },
  backlogDesignDebt: {
    acceptType: ["DESIGN_DEBT"],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "ITEMS_METRICS",
    storeMutation: "setBacklog",
    storeGetter: "GET_DESIGN_DEBT_BACKLOG",
    storeContentGetter: "GET_DESIGN_DEBT_BACKLOG_CONTENT",
    storeBacklogId: "backlogDesignDebt",
    filter: {
      itemTypes: ["DESIGN_DEBT"],
      hasBacklogPriority: true,
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogMetricHeader"
  },
  backlogDev: {
    acceptType: ["DEV"],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "ITEMS_METRICS",
    storeMutation: "setBacklog",
    storeGetter: "GET_DEV_BACKLOG",
    storeContentGetter: "GET_DEV_BACKLOG_CONTENT",
    storeBacklogId: "backlogDev",
    filter: {
      itemTypes: ["DEV"],
      hasBacklogPriority: true,
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogMetricHeader"
  },
  backlogDevOps: {
    acceptType: ["DEV_OPS"],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "ITEMS_METRICS",
    storeMutation: "setBacklog",
    storeGetter: "GET_DEV_OPS_BACKLOG",
    storeContentGetter: "GET_DEV_OPS_BACKLOG_CONTENT",
    storeBacklogId: "backlogDevOps",
    filter: {
      itemTypes: ["DEV_OPS"],
      hasBacklogPriority: true,
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogMetricHeader"
  },
  backlogOps: {
    acceptType: ["OPS"],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "ITEMS_METRICS",
    storeMutation: "setBacklog",
    storeGetter: "GET_OPS_BACKLOG",
    storeContentGetter: "GET_OPS_BACKLOG_CONTENT",
    storeBacklogId: "backlogOps",
    filter: {
      itemTypes: ["OPS"],
      hasBacklogPriority: true,
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogMetricHeader"
  },
  backlogEpic: {
    acceptType: [
      "STORY",
      "TASK",
      "BUG",
      "SPIKE",
      "SUPPORT_CASE",
      "DEV",
      "DEV_OPS",
      "OPS",
      "TECH_DEBT",
      "DESIGN_DEBT",
      "INCIDENT"
    ],
    elementsType: "ITEM",
    backlogGetAction: "GET_EPIC_BACKLOG",
    backlogMetrics: "METRICS_EPIC",
    storeMutation: "setEpicBacklog",
    storeGetter: "GET_EPIC_BACKLOG",
    storeContentGetter: "GET_EPIC_BACKLOG_CONTENT",
    storeBacklogId: "backlogEpic",
    filter: {
      epicIds: [],
      sortKey: ["epicPriority"],
      excludeStoryStatuses: [],
      withLatestStatusChangeComment: true,
      inEpic: true
    },
    priorityType: "EPIC",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    addAction: "ADD_ITEM_TO_EPIC",
    removeAction: "DELETE_ITEM_FROM_EPIC",
    putIntoBacklog: true,
    headerComponentName: ""
  },

  backlogMilestones: {
    acceptType: [],
    elementsType: "EPIC",
    backlogGetAction: "GET_BACKLOG_MILESTONES",
    storeMutation: "setMilestones",
    storeGetter: "GET_BACKLOG_MILESTONES",
    storeBacklogId: "milestones",
    filter: {
      size: 50,
      sortKey: ["title"]
    },
    priorityType: "EPIC",
    addAction: "ADD_EPIC_FROM_MILESTONE",
    priorityAction: "CHANGE_EPIC_PRIORITY_IN_MILESTONE",
    removeAction: "DELETE_EPIC_FROM_MILESTONE",
    putIntoBacklog: true,
    headerComponentName: ""
  },

  roadmapEpic: {
    acceptType: [],
    elementsType: "EPIC",
    backlogGetAction: "GET_EPIC_ROADMAP",
    storeMutation: "setEpicRoadmap",
    storeGetter: "GET_EPIC_ROADMAP",
    storeContentGetter: "GET_EPIC_ROADMAP_CONTENT",
    storeBacklogId: "roadmapEpic",
    filter: {
      sortKey: ["roadMapPriority"],
      archived: false
    },
    priorityType: "EPIC",
    addAction: "",
    removeAction: "DELETE_EPIC_FROM_MILESTONE",
    priorityAction: "CHANGE_EPIC_PRIORITY",
    putIntoBacklog: false,
    headerComponentName: "RoadmapEpicHeader"
  },

  backlogMyWork: {
    acceptType: [],
    elementsType: "ITEM",
    backlogGetAction: "GET_MY_WORK",
    storeMutation: "setBacklog",
    storeGetter: "GET_MY_WORK",
    storeContentGetter: "GET_MY_WORK_CONTENT",
    storeBacklogId: "backlogMyWork",
    filter: {
      teamId: "",
      inActiveSprint: true,
      inNotActiveSprint: false,
      withLatestStatusChangeComment: true
    },
    priorityType: "MY_WORK",
    priorityAction: "MY_WORK_CHANGE_PRIORITY",
    putIntoBacklog: false,
    headerComponentName: ""
  },

  backlogProductAll: {
    acceptType: [],
    elementsType: "ITEM",
    backlogGetAction: "GET_FULL_BACKLOG",
    backlogMetrics: "",
    storeMutation: "setBacklog",
    storeGetter: "GET_PRODUCT_BACKLOG_ALL",
    storeContentGetter: "GET_PRODUCT_BACKLOG_ALL_CONTENT",
    storeBacklogId: "backlogProductAll",
    filter: {
      itemTypes: [],
      size: 20,
      excludeStoryStatuses: [],
      withLatestStatusChangeComment: true
    },
    priorityType: "BACKLOG",
    priorityAction: "",
    headerComponentName: ""
  },

  backlogSingleListView: {
    acceptType: [],
    elementsType: "ITEM",
    backlogGetAction: "GET_BACKLOG",
    backlogMetrics: "ITEMS_METRICS",
    storeMutation: "setBacklog",
    storeGetter: "GET_SINGLE_LIST_VIEW_BACKLOG",
    storeContentGetter: "GET_SINGLE_LIST_VIEW_BACKLOG_CONTENT",
    storeBacklogId: "backlogSingleListView",
    filter: {
      itemTypes: [],
      size: 20,
      excludeStoryStatuses: ["OBSOLETE", "DONE"],
      inSprint: false,
      withLatestStatusChangeComment: true,
      skipTeam: true,
      sortKey: ["backlogPriority", "unprioritizedOrder"]
    },
    priorityType: "SINGLE_LIST",
    priorityAction: "CHANGE_BACKLOG_PRIORITY",
    priorityBatchAction: "CHANGE_BACKLOG_PRIORITY_BATCH",
    putIntoBacklog: true,
    headerComponentName: "BacklogMetricHeader"
  }
}

export class BacklogSetup {
  acceptType: ItemType[]
  elementsType: BacklogElementType
  backlogGetAction: string
  backlogMetrics?: string
  storeMutation: StoreMutation
  storeGetter: string
  storeContentGetter?: string
  storeBacklogId: string
  filter: FilterBacklog
  priorityType: ItemQueryPriorityType
  priorityAction: string
  priorityBatchAction?: string
  addAction?: string
  removeAction?: string
  putIntoBacklog: boolean | undefined
  sortBacklog?: boolean
  headerComponentName: string
  sprintInfo?: SprintInfo
  channels?: any
  backlogType: BacklogType
  backlogSetupId?: string
  skipCanAddItemCheck?: boolean

  static BacklogProduct = new BacklogSetup("backlogProduct")
  static BacklogUnprioritized = new BacklogSetup("backlogUnprioritized")
  static BacklogBug = new BacklogSetup("backlogBug")
  static BacklogIncident = new BacklogSetup("backlogIncident")
  static BacklogEpic = new BacklogSetup("backlogEpic")
  static BacklogSprint = new BacklogSetup("backlogSprint")
  static BacklogSupportCases = new BacklogSetup("backlogSupportCases")
  static BacklogTechDebt = new BacklogSetup("backlogTechDebt")
  static BacklogDesignDebt = new BacklogSetup("backlogDesignDebt")
  static BacklogDev = new BacklogSetup("backlogDev")
  static BacklogDevOps = new BacklogSetup("backlogDevOps")
  static BacklogOps = new BacklogSetup("backlogOps")
  static RoadmapEpic = new BacklogSetup("roadmapEpic")
  static BacklogMilestones = new BacklogSetup("backlogMilestones")
  static BacklogStoryPoker = new BacklogSetup("backlogStoryPoker")
  static BacklogMyWork = new BacklogSetup("backlogMyWork")
  static BacklogProductAll = new BacklogSetup("backlogProductAll")
  static BacklogSingleListView = new BacklogSetup("backlogSingleListView")

  constructor(backlogType: BacklogType) {
    const config = backlogConfigs[backlogType]
    this.backlogType = backlogType
    this.acceptType = config.acceptType
    this.elementsType = config.elementsType
    this.backlogGetAction = config.backlogGetAction
    this.storeMutation = config.storeMutation
    this.backlogMetrics = config.backlogMetrics
    this.storeGetter = config.storeGetter
    this.storeContentGetter = config.storeContentGetter
    this.storeBacklogId = config.storeBacklogId
    this.filter = FilterBacklog.create(config.filter)
    this.priorityType = config.priorityType
    this.priorityAction = config.priorityAction
    this.priorityBatchAction = config.priorityBatchAction
    this.addAction = config.addAction
    this.removeAction = config.removeAction
    this.putIntoBacklog = config.putIntoBacklog
    this.sortBacklog = config.sortBacklog
    this.headerComponentName = config.headerComponentName
    this.backlogSetupId = this.storeBacklogId
    this.channels = {}
  }

  checkWipLimitExceeded(contentLength) {
    if (!this.sprintInfo?.wip || this.sprintInfo?.wipLimitExceeded === undefined) return

    if (this.sprintInfo.wip > contentLength) {
      this.sprintInfo.wipLimitExceeded = false
    } else {
      this.sprintInfo.wipLimitExceeded = true
    }
  }

  setWipLimitExceeded(val: boolean) {
    if (!this.sprintInfo?.wip || this.sprintInfo?.wipLimitExceeded === undefined) return
    if (this.sprintInfo) {
      this.sprintInfo.wipLimitExceeded = val
    }
  }

  set setFilter(val) {
    val === null ? this.resetFilters() : (this.filter = Object.assign(this.filter, val))
    if (this.backlogSetupId) {
      emitterEmit(this.backlogSetupId)
    }
    this.publish("backlogFilterChanged", this.filter)
  }

  resetFilters() {
    if (this.backlogType.includes("backlogSprint")) {
      const { sprintIds, inSprint, currentProjectId } = this.filter
      this.filter = Object.assign(FilterBacklog.create(backlogConfigs[this.backlogType].filter), {
        sprintIds,
        currentProjectId,
        inSprint
      })
    } else {
      this.filter = FilterBacklog.create(backlogConfigs[this.backlogType].filter)
    }
  }

  canAddItem(draggedItem: any): boolean {
    switch (this.elementsType) {
      case "ITEM":
        return this.acceptType?.length
          ? this.acceptType.includes(draggedItem.itemTypeParsed.id)
          : true
      case "EPIC":
        return !this.filter?.teamIds?.length
      default:
        return false
    }
  }

  get isEpicPriority(): boolean {
    return this.priorityType === "EPIC"
  }

  get isItemType(): boolean {
    return this.elementsType === "ITEM"
  }

  get metric() {
    if (!this.backlogMetrics) return false
    if (!this.priorityType.length) return false

    switch (this.priorityType) {
      case "BACKLOG":
        return {
          action: this.backlogMetrics,
          itemType: this.filter.itemTypes,
          type: this.priorityType
        }
      case "EPIC":
        return {
          action: this.backlogMetrics,
          type: this.priorityType
        }
      case "SPRINT":
        return {
          action: this.backlogMetrics,
          type: this.priorityType
        }
      default:
        return {}
    }
  }

  get sprintOverdue() {
    if (!this.sprintInfo || this.sprintInfo.status !== "NOT_STARTED") return false
    return !isBefore(getTodayCalendarDate(), toCalendarDate(this.sprintInfo.startDate))
  }

  publish(channelName, data) {
    const channel = this.channels[channelName]
    if (!channel || !channel.length) return

    channel.forEach(listener => listener(data))
  }

  static canMoveItemFromSprint({ item, sprintStatus }: { item: Item; sprintStatus: string }) {
    if (sprintStatus !== "STARTED") {
      return true
    }
    const itemNotInProgress = ItemNotInProgressStatuses.find(
      status => status === item.statusParsed?.id
    )
    if (!itemNotInProgress) {
      return false
    }
    if (item.isCompleted) {
      return false
    }
    return true
  }
}

export class BacklogSprintSetup extends BacklogSetup {
  constructor(sprintInfo?: SprintInfo) {
    super("backlogSprint")

    if (!sprintInfo) return

    this.sprintInfo = sprintInfo
    this.filter.sprintIds = [sprintInfo.id]
    this.filter.inSprint = true
    this.backlogSetupId = this.storeBacklogId + `_${this.sprintInfo?.sequenceNumber}`
  }

  canAddItem(draggedItem: Item) {
    if (draggedItem.statusParsed?.id === "DONE") {
      return true
    }

    if (this.sprintInfo?.wipLimitExceeded) {
      if (draggedItem.isSupportCase) {
        return draggedItem.isEstimated
      }

      return false
    }

    if (this.sprintInfo && this.sprintInfo.status !== "STARTED") {
      return true
    }

    if (this.sprintInfo?.continuousSprint) {
      return draggedItem.isEstimatedWithoutStoryPoints
    }

    return draggedItem.isEstimated
  }

  get isNotStartedSprint(): boolean {
    return this.sprintInfo?.status === "NOT_STARTED"
  }
}

export type BacklogTab = {
  id: string
  label: string
  backlogSetup?: BacklogSetup
  status?: string
  startDate?: string
  endDate?: string
  filterId?: ItemType[]
  metricKey?: string
  countOptions?: BacklogTabCountOptions
}

export interface BacklogTabCountOptions {
  number: string
  notify?: string
}

export interface RemoveItemPayload {
  backlogSetup: BacklogSetup
  item: Item
}

export interface BacklogFilterOption {
  title: string
  modalConfig: BacklogFilterModalOption
  componentConfig: BacklogFilterComponentOption
}

export interface BacklogFilterModalOption {
  title: string
  width: string
}

export interface BacklogFilterComponentOption {
  name: string
  id?: string
  customId?: string
  placeholder?: string
}

export interface ChangePriorityMessageParams {
  newIndex: number
  oldIndex?: number
  item: Item | Epic
  toBacklogId: string
  fromBacklogId: string
  toBacklogSprintNumber?: string
  fromBacklogSprintNumber?: string
}

export const paramsForUnprioritizedMetrics = (teamIds: string[]) => {
  const excludeStoryStatuses = ["OBSOLETE", "DONE"] as ItemStatus[]
  const props = {
    teamIds,
    inSprint: false,
    hasBacklogPriority: false,
    excludeStoryStatuses,
    includeTeamLessSupportCases: true
  }
  return new FilterBacklogMetrics(props)
}
